import { ButtonBase, Tooltip } from '@mui/material';
import { ComponentProps, MouseEvent, RefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    structureRowAnchor,
    useStructureColorOpener,
} from '~/hooks/structure-set/useStructureColorEdit';
import { StructureColorEditSource } from '~/state/atoms/structureSet/roiAtoms';
import { dicomColorToRgbString } from '~/utils/color';
import RoiColor from './RoiColor';

/**
 * A structure's colour dot as the button that opens its colour edit, and
 * closes it again on a later click; a double-click counts as one click. A
 * plain dot while the structure's colour edit is withheld. The popover
 * opens beside the structure's row in the list, or beside
 * `anchorFallbackRef` when the row is not on screen. Shows the colour
 * being edited while an edit is open. The pointer over it shows a ring in
 * the dot's own colour, a small gap out from it, which needs that much room
 * around the dot: a container that clips at the dot's edge cuts it off.
 */
export default function StructureColorButton({
    roi,
    source,
    anchorFallbackRef,
    size = 10,
    ...dot
}: {
    roi: RoiReadModel;
    source: StructureColorEditSource;
    anchorFallbackRef?: RefObject<HTMLElement | null>;
    /** Size of the dot, in CSS pixels. */
    size?: number;
} & Pick<
    ComponentProps<typeof RoiColor>,
    'isEmptyVolume' | 'isMarker' | 'isTarget'
>) {
    const { color, withheldReasonId, toggle, openerProps } =
        useStructureColorOpener(roi, source);
    const marker = <RoiColor color={color} size={size} {...dot} />;
    if (withheldReasonId !== undefined) {
        return marker;
    }

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
        // The row beneath selects on click; this click is the button's.
        event.stopPropagation();
        event.preventDefault();
        // The later clicks of a double-click belong to the press that the
        // first one answered: they are not presses to close the edit again.
        if (event.detail > 1) {
            return;
        }
        const fallback = anchorFallbackRef?.current ?? event.currentTarget;
        toggle(structureRowAnchor(roi.roiNumber, fallback) ?? fallback);
    };

    return (
        <Tooltip
            title={<FormattedMessage id="colorPicker.changeColorTooltip" />}
            disableInteractive
        >
            <ButtonBase
                disableRipple
                {...openerProps}
                data-testid={`structure-color-button-${roi.roiNumber}`}
                onClick={handleOpen}
                onContextMenu={handleOpen}
                onDoubleClick={(event) => event.stopPropagation()}
                sx={{
                    // A larger target than the dot it draws.
                    p: 1,
                    m: -1,
                    '&:focus-visible': {
                        outline: 1,
                        outlineColor: 'primary.main',
                        outlineOffset: -5,
                    },
                    '& > span': {
                        display: 'flex',
                        borderRadius: '50%',
                        outline: '1.5px solid transparent',
                        outlineOffset: 2,
                        transition: 'outline-color 200ms ease',
                    },
                    '&:hover > span': {
                        outlineColor: dicomColorToRgbString(color),
                    },
                }}
            >
                <span>{marker}</span>
            </ButtonBase>
        </Tooltip>
    );
}
